import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import type { PsychologicalResult } from '../../model';
import parseIndividualMotivation from './parseIndividualMotivation';
import parseMl360 from './parseMl360';
import parseSelfDetermination from './parseSelfDetermination';
import parseTeamResilience from './parseTeamResilience';
import {
    buildIndividualMotivationPlaceholder,
    buildMl360Placeholder,
    buildSelfDeterminationPlaceholder,
    buildTeamResiliencePlaceholder,
} from './placeholders';

export const selfDeterminationHeight = 355;
export const teamResilienceHeight = 340;
export const ml360Height = 452;
export const individualMotivationHeight = 355;

const parsePsychologicalResults = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[],
    teamResilienceItems: AssessmentResultItemDataFragment[],
    incompleteItems: AssessmentResultItemDataFragment[]
): PsychologicalResult[] => {
    const incompleteModuleInstanceIds = new Set(incompleteItems.map(i => i.moduleInstance?.id));

    const completedItems = items.filter(
        i => !i.moduleInstance?.id || !incompleteModuleInstanceIds.has(i.moduleInstance.id)
    );
    const completedTeamResilienceItems = teamResilienceItems.filter(
        i => !i.moduleInstance?.id || !incompleteModuleInstanceIds.has(i.moduleInstance.id)
    );

    const results: PsychologicalResult[] = [];

    // Self Determination
    const completedSelfDetermination = parseSelfDetermination(t, completedItems);
    if (completedSelfDetermination) {
        results.push(completedSelfDetermination);
    } else {
        const incompleteSelfDetermination = incompleteItems.find(
            i => i.result.__typename === 'SelfDeterminationResult'
        );
        if (incompleteSelfDetermination) {
            const p = buildSelfDeterminationPlaceholder(t, incompleteSelfDetermination);
            if (p) {
                results.push(p);
            }
        }
    }

    // Individual Motivation
    const completedIndividualMotivation = parseIndividualMotivation(t, completedItems);
    if (completedIndividualMotivation) {
        results.push(completedIndividualMotivation);
    } else {
        const incompleteIndividualMotivation = incompleteItems.find(
            i => i.result.__typename === 'IndividualMotivationResult'
        );
        if (incompleteIndividualMotivation) {
            const p = buildIndividualMotivationPlaceholder(t, incompleteIndividualMotivation);
            if (p) {
                results.push(p);
            }
        }
    }

    // Team Resilience
    const completedTeamResilience = parseTeamResilience(t, completedItems, completedTeamResilienceItems);
    if (completedTeamResilience) {
        results.push(completedTeamResilience);
    } else {
        const incompleteTeamResilience = incompleteItems.find(i => i.result.__typename === 'TeamResilienceResult');
        if (incompleteTeamResilience) {
            const p = buildTeamResiliencePlaceholder(t, incompleteTeamResilience);
            if (p) {
                results.push(p);
            }
        }
    }

    // ML360 (single aggregated section)
    const ml360CombinedItems = [...completedItems, ...incompleteItems].filter(
        i => i.result.__typename === 'ML360Result'
    );
    if (ml360CombinedItems.length > 0) {
        const completedMl360 = parseMl360(t, ml360CombinedItems);
        if (completedMl360) {
            results.push(completedMl360);
        } else {
            const p = buildMl360Placeholder(t, ml360CombinedItems);
            if (p) {
                results.push(p);
            }
        }
    }

    return results;
};

export default parsePsychologicalResults;
